import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["item", "modal"];

  connect() {
    this._currentIndex = -1;
    this._handler = this._onKey.bind(this);
    document.addEventListener("keydown", this._handler);
  }

  disconnect() {
    document.removeEventListener("keydown", this._handler);
  }

  _onKey(event) {
    const tag = event.target.tagName;
    if (
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      event.target.isContentEditable
    ) {
      return;
    }

    switch (event.key) {
      case "j":
        event.preventDefault();
        this._moveFocus(1);
        break;
      case "k":
        event.preventDefault();
        this._moveFocus(-1);
        break;
      case "e":
        event.preventDefault();
        this._actOnFocused("done");
        break;
      case "#":
        event.preventDefault();
        this._actOnFocused("deleted");
        break;
      case "d":
        event.preventDefault();
        this._actOnFocused("deferred");
        break;
      case "Enter":
        event.preventDefault();
        this._openFocused();
        break;
      case "?":
        event.preventDefault();
        this.toggleModal();
        break;
    }
  }

  _moveFocus(delta) {
    const items = this.itemTargets;
    if (items.length === 0) return;

    if (this._currentIndex >= 0 && items[this._currentIndex]) {
      items[this._currentIndex].classList.remove(
        "ring-2",
        "ring-indigo-500",
        "ring-offset-1",
      );
    }

    this._currentIndex = Math.max(
      0,
      Math.min(items.length - 1, this._currentIndex + delta),
    );

    const focused = items[this._currentIndex];
    focused.classList.add("ring-2", "ring-indigo-500", "ring-offset-1");
    focused.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  _focusedItem() {
    if (this._currentIndex < 0) return null;
    return this.itemTargets[this._currentIndex] ?? null;
  }

  _actOnFocused(status) {
    if (this._busy) return;
    const item = this._focusedItem();
    if (!item) return;

    const dispositionUrl = item.dataset.dispositionUrl;
    if (!dispositionUrl) return;

    const body = new URLSearchParams({ status });
    this._busy = true;

    fetch(dispositionUrl, {
      method: "PATCH",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
          ?.content,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    })
      .then((res) => {
        if (res.ok) {
          return res.text().then((html) => {
            Turbo.renderStreamMessage(html);
            requestAnimationFrame(() => this._realignAfterRemoval());
          });
        }
      })
      .catch(() => {
        // Network error — index stays valid, no DOM change needed
      })
      .finally(() => {
        this._busy = false;
      });
  }

  _realignAfterRemoval() {
    const items = this.itemTargets;
    if (items.length === 0) {
      this._currentIndex = -1;
      return;
    }
    this._currentIndex = Math.min(this._currentIndex, items.length - 1);
    if (this._currentIndex >= 0) {
      items[this._currentIndex].classList.add(
        "ring-2",
        "ring-indigo-500",
        "ring-offset-1",
      );
      items[this._currentIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }

  _openFocused() {
    const item = this._focusedItem();
    if (!item) return;
    const link = item.querySelector("a[href]");
    if (link) Turbo.visit(link.href);
  }

  toggleModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.toggle("hidden");
    }
  }

  stopProp(event) {
    event.stopPropagation();
  }
}
